import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveTelemetryHealth } from '@/lib/telemetry-health';
import {
  type OnboardingStatus,
  type OnboardingChecklistInput,
  type OnboardingChecklistResult,
  resolveOnboardingChecklist,
} from '@/lib/onboarding-checklist';
import { batchedQuery } from '@/lib/batched-query';

// ─── Types ──────────────────────────────────────────────────────────────

export interface OnboardingChecklistRow extends OnboardingChecklistResult {
  property_id: string;
  property_name: string;
  provider_type: string;
  org_name: string | null;
}

export interface OnboardingChecklistSummary {
  not_started: number;
  connected_no_data: number;
  data_flowing: number;
  needs_configuration: number;
  needs_verification: number;
  report_ready: number;
  production_ready: number;
  blocked: number;
  total: number;
  avg_completion_pct: number;
}

export interface OnboardingChecklistData {
  rows: OnboardingChecklistRow[];
  summary: OnboardingChecklistSummary;
}

// ─── Fetch ──────────────────────────────────────────────────────────────

async function fetchOnboardingChecklist(): Promise<OnboardingChecklistData> {
  // 1. All properties with service provider
  const { data: properties, error: propErr } = await supabase
    .from('properties')
    .select('id, name, service_provider_org_id, battery_kwh_total, electricity_rate_cents_per_kwh')
    .not('service_provider_org_id', 'is', null);

  if (propErr) throw propErr;
  if (!properties?.length) return { rows: [], summary: emptySummary() };

  const propIds = properties.map((p) => p.id);

  // 2. Energy systems (polling fields) — batched for scale safety
  const systems = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('energy_systems')
      .select(`
        id, property_id,
        last_poll_at, last_poll_status,
        last_provider_ok_at, last_provider_auth_failed_at,
        last_non_empty_telemetry_at,
        consecutive_failures
      `)
      .in('property_id', chunk);
    return data ?? [];
  });

  const systemsByProp = new Map<string, (typeof systems)>();
  for (const s of systems) {
    const list = systemsByProp.get(s.property_id) ?? [];
    list.push(s);
    systemsByProp.set(s.property_id, list);
  }

  // 3. Plants (capacity + topology) — batched
  const plants = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('plants')
      .select('id, property_id, provider_type, battery_kwh_configured, battery_topology_override')
      .in('property_id', chunk);
    return data ?? [];
  });

  const plantByProp = new Map<string, (typeof plants)[number]>();
  for (const p of plants) {
    if (!plantByProp.has(p.property_id)) {
      plantByProp.set(p.property_id, p);
    }
  }

  // 4. Organizations
  const orgIds = [...new Set(properties.map((p) => p.service_provider_org_id).filter(Boolean))];
  const { data: orgs } = await supabase
    .from('organizations')
    .select('id, name')
    .in('id', orgIds);

  const orgNameById = new Map((orgs ?? []).map((o) => [o.id, o.name]));

  // 5. Daily energy summaries (30d window + total count) — batched for scale safety
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const summaries = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('daily_energy_summary')
      .select('property_id, date, pv_energy_wh, load_energy_wh, grid_imported_wh, grid_exported_wh')
      .in('property_id', chunk)
      .gte('date', sixtyDaysAgo);
    return data ?? [];
  });

  const totalCounts = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('daily_energy_summary')
      .select('property_id, date')
      .in('property_id', chunk);
    return data ?? [];
  });

  // Build summary stats
  const summaryStats = new Map<string, {
    days_30: number;
    total: number;
    all_zero: boolean;
  }>();

  for (const pid of propIds) {
    summaryStats.set(pid, { days_30: 0, total: 0, all_zero: true });
  }

  for (const row of totalCounts) {
    const stat = summaryStats.get(row.property_id);
    if (stat) stat.total++;
  }

  for (const row of summaries) {
    const stat = summaryStats.get(row.property_id);
    if (stat) {
      if (row.date >= thirtyDaysAgo) stat.days_30++;
      const totalEnergy = (row.pv_energy_wh ?? 0) + (row.load_energy_wh ?? 0) +
        (row.grid_imported_wh ?? 0) + (row.grid_exported_wh ?? 0);
      if (totalEnergy > 0) stat.all_zero = false;
    }
  }

  // 6. Report subscriptions — batched
  const subs = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('customer_report_subscriptions')
      .select('property_id, enabled')
      .in('property_id', chunk);
    return data ?? [];
  });

  const subByProp = new Map(subs.map((s) => [s.property_id, s]));

  // 7. Battery health snapshots — batched
  const plantIds = plants.map((p) => p.id);
  const snapshots = await batchedQuery(plantIds, async (chunk) => {
    const { data } = await supabase
      .from('battery_health_snapshots')
      .select('plant_id, created_at, computed_capacity_kwh')
      .in('plant_id', chunk)
      .not('computed_capacity_kwh', 'is', null)
      .order('created_at', { ascending: false });
    return data ?? [];
  });

  const snapshotByPlant = new Map<string, string>();
  for (const s of snapshots) {
    if (!snapshotByPlant.has(s.plant_id)) {
      snapshotByPlant.set(s.plant_id, s.created_at);
    }
  }

  // 8. Topology (multi-inverter)
  const multiInverterPropIds = propIds.filter((pid) => {
    const sysList = systemsByProp.get(pid) ?? [];
    const hasBattery = (properties.find((p) => p.id === pid)?.battery_kwh_total ?? 0) > 0;
    return sysList.length >= 2 && hasBattery;
  });

  let topologyMap = new Map<string, { confidence: string; review_required: boolean }>();
  if (multiInverterPropIds.length > 0) {
    const multiPlantIds = multiInverterPropIds
      .map((pid) => plantByProp.get(pid)?.id)
      .filter(Boolean) as string[];

    if (multiPlantIds.length > 0) {
      const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
      const { data: topoStats } = await supabase.rpc('admin_battery_topology_stats', {
        p_plant_ids: multiPlantIds,
        p_since: threeDaysAgo,
      });

      for (const pid of multiPlantIds) {
        const plant = (plants ?? []).find((p) => p.id === pid);
        const override = plant?.battery_topology_override as string | null;
        const stats = (topoStats as Array<Record<string, unknown>> ?? []).find(
          (s) => s.plant_id === pid,
        );
        const propId = plant?.property_id;
        const systemCount = propId ? (systemsByProp.get(propId)?.length ?? 2) : 2;

        if (override === 'shared_bank' || override === 'independent_banks') {
          topologyMap.set(pid, { confidence: 'high', review_required: false });
        } else if (!stats || Number(stats.overlapping_buckets ?? 0) < 20) {
          topologyMap.set(pid, { confidence: 'low', review_required: true });
        } else {
          const avg = Number(stats.avg_soc_delta ?? 0);
          const max = Number(stats.max_soc_delta ?? 0);
          const p95 = Number(stats.p95_soc_delta ?? 0);
          const sustained = Number(stats.sustained_divergence_count ?? 0);
          const buckets = Number(stats.overlapping_buckets ?? 0);

          if (avg <= 1.0 && max <= 3.0) {
            topologyMap.set(pid, { confidence: buckets >= 50 ? 'high' : 'medium', review_required: false });
          } else if (avg >= 5.0 || p95 >= 10.0 || sustained >= 3) {
            topologyMap.set(pid, {
              confidence: systemCount > 2 ? 'medium' : (buckets >= 50 ? 'high' : 'medium'),
              review_required: systemCount > 2,
            });
          } else {
            topologyMap.set(pid, { confidence: 'low', review_required: true });
          }
        }
      }
    }
  }

  // 9. Build rows
  const now = Date.now();
  const rows: OnboardingChecklistRow[] = properties.map((prop) => {
    const sysList = systemsByProp.get(prop.id) ?? [];
    const sys = sysList[0] ?? null;
    const plant = plantByProp.get(prop.id);
    const stat = summaryStats.get(prop.id)!;
    const sub = subByProp.get(prop.id);
    const hasBattery = (prop.battery_kwh_total ?? 0) > 0;
    const isMulti = sysList.length >= 2 && hasBattery;
    const topo = plant ? topologyMap.get(plant.id) : undefined;
    const snapshot = plant ? snapshotByPlant.get(plant.id) : undefined;

    // Resolve capacity (inline version of configured-capacity-resolver)
    const plantKwh = plant?.battery_kwh_configured != null ? Number(plant.battery_kwh_configured) : 0;
    const propKwh = prop.battery_kwh_total != null ? Number(prop.battery_kwh_total) : 0;
    let capacityKwh: number | null = null;
    let capacitySource: 'plant_configured' | 'property_total_fallback' | 'missing' = 'missing';
    let capacityConfidence: 'verified' | 'needs_verification' | 'missing' = 'missing';
    let possibleDefault = false;

    if (plantKwh > 0) {
      capacityKwh = plantKwh;
      capacitySource = 'plant_configured';
      capacityConfidence = 'verified';
      possibleDefault = plantKwh === 30.0;
    } else if (propKwh > 0) {
      capacityKwh = propKwh;
      capacitySource = 'property_total_fallback';
      capacityConfidence = 'needs_verification';
      possibleDefault = propKwh === 30.0;
    }

    // Resolve telemetry health status
    const healthInput = {
      last_provider_ok_at: sys?.last_provider_ok_at ?? null,
      last_non_empty_telemetry_at: sys?.last_non_empty_telemetry_at ?? null,
      last_poll_at: sys?.last_poll_at ?? null,
      last_poll_status: sys?.last_poll_status ?? null,
      last_provider_auth_failed_at: sys?.last_provider_auth_failed_at ?? null,
      consecutive_failures: sys?.consecutive_failures ?? 0,
      days_with_data_30: stat.days_30,
      total_daily_summaries: stat.total,
      all_summaries_zero: stat.all_zero,
      now,
    };
    const telemetryResult = resolveTelemetryHealth(healthInput);

    const checklistInput: OnboardingChecklistInput = {
      property_id: prop.id,
      property_name: prop.name ?? 'Unknown',
      provider_type: plant?.provider_type ?? 'unknown',
      org_name: orgNameById.get(prop.service_provider_org_id) ?? null,
      system_count: sysList.length,

      telemetry_status: telemetryResult.status,
      days_with_data_30: stat.days_30,
      total_daily_summaries: stat.total,
      last_poll_at: sys?.last_poll_at ?? null,
      last_poll_status: sys?.last_poll_status ?? null,
      consecutive_failures: sys?.consecutive_failures ?? 0,

      capacity_kwh: capacityKwh,
      capacity_source: capacitySource,
      capacity_confidence: capacityConfidence,
      possible_default_capacity: possibleDefault,
      has_battery: hasBattery,

      has_battery_snapshot: !!snapshot,
      last_snapshot_date: snapshot ?? null,

      is_multi_inverter: isMulti,
      topology_confidence: topo?.confidence ?? null,
      topology_review_required: topo?.review_required ?? false,

      has_report_subscription: !!sub,
      report_enabled: sub?.enabled ?? false,
      has_electricity_rate: (prop.electricity_rate_cents_per_kwh ?? 0) > 0,
      report_days_with_data: stat.days_30,
    };

    const result = resolveOnboardingChecklist(checklistInput);

    return {
      property_id: prop.id,
      property_name: prop.name ?? 'Unknown',
      provider_type: plant?.provider_type ?? 'unknown',
      org_name: orgNameById.get(prop.service_provider_org_id) ?? null,
      ...result,
    };
  }).sort((a, b) => {
    // Blocked first, then by completion pct ascending
    const statusOrder: Record<OnboardingStatus, number> = {
      blocked: 0,
      not_started: 1,
      connected_no_data: 2,
      data_flowing: 3,
      needs_configuration: 4,
      needs_verification: 5,
      report_ready: 6,
      production_ready: 7,
    };
    const diff = statusOrder[a.overall_status] - statusOrder[b.overall_status];
    if (diff !== 0) return diff;
    return a.completion_pct - b.completion_pct;
  });

  // 10. Summary
  const summary = buildSummary(rows);

  return { rows, summary };
}

function buildSummary(rows: OnboardingChecklistRow[]): OnboardingChecklistSummary {
  const counts: Record<OnboardingStatus, number> = {
    not_started: 0,
    connected_no_data: 0,
    data_flowing: 0,
    needs_configuration: 0,
    needs_verification: 0,
    report_ready: 0,
    production_ready: 0,
    blocked: 0,
  };

  let totalPct = 0;
  for (const row of rows) {
    counts[row.overall_status]++;
    totalPct += row.completion_pct;
  }

  return {
    ...counts,
    total: rows.length,
    avg_completion_pct: rows.length > 0 ? Math.round(totalPct / rows.length) : 0,
  };
}

function emptySummary(): OnboardingChecklistSummary {
  return {
    not_started: 0,
    connected_no_data: 0,
    data_flowing: 0,
    needs_configuration: 0,
    needs_verification: 0,
    report_ready: 0,
    production_ready: 0,
    blocked: 0,
    total: 0,
    avg_completion_pct: 0,
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useAdminOnboardingChecklist(enabled: boolean, refetchInterval: number | false) {
  return useQuery({
    queryKey: ['admin-onboarding-checklist'],
    queryFn: fetchOnboardingChecklist,
    enabled,
    refetchInterval,
    staleTime: 60_000,
  });
}
